import type { ClientListItem, ClientStatus } from '@fos/shared/contracts/crm';
import { CLIENT_STATUS_PLURAL_LABELS } from '@fos/shared/labels';
import { keepPreviousData } from '@tanstack/react-query';
import { ChevronDown, Plus, Search, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useActiveSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Avatar } from '../../../ui/Avatar';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { Input } from '../../../ui/Input';
import { Menu, MenuContent, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented, type SegmentedOption } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { Table, TBody, Td, THead, Th, Tr } from '../../../ui/Table';
import { MobileFab, SEGMENTED_TOUCH, useIsPhone } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { activeProjectsLabel, lastContactLabel, unbilledLabel, websiteLabel } from '../client-format';
import { ClientFormDialog } from '../components/ClientFormDialog';
import { ClientTile } from '../components/ClientTile';

type View = 'active' | 'prospect' | 'inactive' | 'archived';
const ALL_OWNERS = '__all__';
const NO_OWNER = '__none__';

/** Opóźnienie wyszukiwania (bez zapytania na każdy znak). */
function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/** Sygnał w ostatniej kolumnie (Figma: „1 do akceptacji”, „Zadanie po terminie”). */
function ClientSignal({ client }: { client: ClientListItem }) {
  const { pendingApprovals, overdueTasks } = client.stats;
  if (pendingApprovals) return <StatusPill tone="accent">{pendingApprovals} do akceptacji</StatusPill>;
  if (overdueTasks) {
    return (
      <StatusPill tone="danger">
        {overdueTasks === 1
          ? 'Zadanie po terminie'
          : `${overdueTasks} ${plural(overdueTasks, ['zadanie', 'zadania', 'zadań'])} po terminie`}
      </StatusPill>
    );
  }
  if (client.partial) return <span className="text-caption text-fg-muted">Tylko podgląd</span>;
  return null;
}

function OwnerCell({ client }: { client: ClientListItem }) {
  if (!client.owner) return <span className="text-fg-muted">–</span>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar initials={client.owner.initials} color={client.owner.avatarColor} size={24} />
      <span className="truncate text-fg-secondary">{client.owner.name.split(' ')[0]}</span>
    </span>
  );
}

/**
 * Lista klientów (Figma 05 Klienci, 3031:3134): tytuł z liczbą, „Nowy klient”, zakładki Aktywni / Potencjalni /
 * Archiwum, wyszukiwarka, filtr opiekuna, tabela (klient z kafelkiem i stroną, opiekun, projekty, do rozliczenia,
 * ostatni kontakt, sygnał). Mobile: lista bez kart.
 */
export function ClientsListPage() {
  const access = useAccess();
  if (!access.can('clients')) return <ModulePage module="clients" />;
  return <ClientsList />;
}

function ClientsList() {
  const access = useAccess();
  const session = useActiveSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canEdit = access.can('clients', 'edit');
  const seesRates = access.flag('seesRates');

  const view =
    (['active', 'prospect', 'inactive', 'archived'] as const).find((v) => v === params.get('widok')) ?? 'active';
  const owner = params.get('opiekun') ?? ALL_OWNERS;
  const [q, setQ] = useState(params.get('q') ?? '');
  const debouncedQ = useDebounced(q.trim());
  const [createOpen, setCreateOpen] = useState(params.get('nowy') === '1');
  const phone = useIsPhone();

  const update = (patch: Record<string, string | null>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  };

  // „Nowy klient” z menu Dodaj / ⌘K: /klienci?nowy=1
  const newParam = params.get('nowy');
  // biome-ignore lint/correctness/useExhaustiveDependencies: reakcja tylko na parametr
  useEffect(() => {
    if (newParam === '1') {
      if (canEdit) setCreateOpen(true);
      update({ nowy: null });
    }
  }, [newParam]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: synchronizacja wyszukiwania z adresem
  useEffect(() => {
    if ((params.get('q') ?? '') !== debouncedQ) update({ q: debouncedQ || null });
  }, [debouncedQ]);

  const input = {
    status: view as ClientStatus,
    q: debouncedQ || undefined,
    ownerUserId: owner === ALL_OWNERS ? undefined : owner === NO_OWNER ? null : owner,
  };
  const list = trpc.clients.list.useQuery(input, { placeholderData: keepPreviousData });
  const members = trpc.team.listMembers.useQuery(undefined, { staleTime: 60_000 });

  const counts = list.data?.counts;
  const viewOptions = useMemo<SegmentedOption<View>[]>(() => {
    const views: View[] = ['active', 'prospect'];
    if ((counts?.inactive ?? 0) > 0 || view === 'inactive') views.push('inactive');
    views.push('archived');
    return views.map((v) => ({ value: v, label: CLIENT_STATUS_PLURAL_LABELS[v] }));
  }, [counts?.inactive, view]);

  const ownerOptions = useMemo(() => {
    const active = (members.data ?? []).filter((m) => m.status === 'active');
    return [
      { value: ALL_OWNERS, label: 'wszyscy' },
      { value: session.user.id, label: 'ja' },
      ...active.filter((m) => m.userId !== session.user.id).map((m) => ({ value: m.userId, label: m.name })),
      { value: NO_OWNER, label: 'bez opiekuna' },
    ];
  }, [members.data, session.user.id]);
  const ownerLabel = ownerOptions.find((o) => o.value === owner)?.label ?? 'wszyscy';

  const filtered = Boolean(debouncedQ) || owner !== ALL_OWNERS;
  const items = list.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        title={
          <>
            Klienci
            {/* lifting 17.10.2026: licznik w nagłówku jako mała pigułka */}
            {list.data && <CountPill count={list.data.total} className="ml-2.5 align-middle tracking-normal" />}
          </>
        }
        actions={
          canEdit && (
            <Button variant="primary" icon={Plus} onClick={() => setCreateOpen(true)} className="max-md:hidden">
              Nowy klient
            </Button>
          )
        }
      />
      {canEdit && phone && (
        <MobileFab icon={Plus} onClick={() => setCreateOpen(true)}>
          Nowy klient
        </MobileFab>
      )}

      <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center">
        <Segmented
          aria-label="Status klientów"
          value={view}
          onValueChange={(v) => update({ widok: v === 'active' ? null : v })}
          options={viewOptions}
          className={`max-w-full self-start overflow-x-auto ${SEGMENTED_TOUCH}`}
        />
        <span className="hidden flex-1 md:block" />
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 md:w-[220px] md:flex-none">
            <Input
              size="s"
              className="max-md:h-11"
              icon={Search}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Szukaj klienta"
              aria-label="Szukaj klienta lub kontaktu"
            />
          </div>
          <Menu>
            <MenuTrigger asChild>
              <Button
                variant="secondary"
                iconRight={ChevronDown}
                className={cn(
                  'shrink-0 font-normal text-fg-secondary max-md:h-11',
                  // aktywny filtr: tło i tekst akcentu (lifting 17.10.2026)
                  owner !== ALL_OWNERS && 'border-transparent bg-accent-subtle text-accent hover:bg-accent-subtle',
                )}
              >
                Opiekun: {ownerLabel}
              </Button>
            </MenuTrigger>
            <MenuContent align="end">
              <MenuLabel>Opiekun</MenuLabel>
              <MenuRadioGroup value={owner} onValueChange={(v) => update({ opiekun: v === ALL_OWNERS ? null : v })}>
                {ownerOptions.map((o) => (
                  <MenuRadioItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuContent>
          </Menu>
        </div>
      </div>

      <Section className="mt-5">
        {list.isPending ? (
          <ListSkeleton rows={5} />
        ) : list.isError ? (
          <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : items.length === 0 ? (
          filtered ? (
            <EmptyState
              icon={Search}
              title="Brak klientów dla tych filtrów"
              description="Zmień wyszukiwanie albo opiekuna."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setQ('');
                    update({ q: null, opiekun: null });
                  }}
                >
                  Wyczyść filtry
                </Button>
              }
            />
          ) : view === 'archived' ? (
            <EmptyState
              icon={Users}
              title="Archiwum jest puste"
              description="Tu trafiają klienci, z którymi nie pracujesz."
            />
          ) : view === 'prospect' ? (
            <EmptyState
              icon={Users}
              title="Brak potencjalnych klientów"
              description="Nowe zapytania oznacz statusem „Potencjalny”."
            />
          ) : (
            <EmptyState
              icon={Users}
              title="Nie masz jeszcze klientów"
              description="Dodaj pierwszego klienta, żeby prowadzić jego projekty, zadania i czas."
              action={
                canEdit && (
                  <Button variant="primary" icon={Plus} onClick={() => setCreateOpen(true)}>
                    Nowy klient
                  </Button>
                )
              }
            />
          )
        ) : (
          // lista na telefon przed tabelą: ostatni wiersz tabeli bez linii (ui/Section), lista robi to sama
          <div className={cn('transition-opacity', list.isPlaceholderData && 'opacity-60')}>
            <MobileList items={items} />
            <DesktopTable items={items} seesRates={seesRates} />
          </div>
        )}
      </Section>

      {canEdit && (
        <ClientFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSaved={(client) => void navigate(`/klienci/${client.id}`)}
        />
      )}
    </Page>
  );
}

/**
 * Tabela od 1024 px. Kolumny o mniejszym znaczeniu („Do rozliczenia”, „Ostatni kontakt”) dopiero od 1280 px,
 * żeby nazwa klienta zawsze miała miejsce (szerokości na nagłówkach: ukryta komórka nie tworzy kolumny).
 * Poniżej 1024 px lista jak na telefonie.
 */
function DesktopTable({ items, seesRates }: { items: ClientListItem[]; seesRates: boolean }) {
  return (
    <div className="hidden lg:block">
      <Table aria-label="Klienci" className="table-fixed">
        <THead>
          <Tr>
            <Th className="pl-1">Klient</Th>
            <Th className="w-[126px] pl-4">Opiekun</Th>
            <Th className="w-[126px] pl-4">Projekty</Th>
            {seesRates && (
              <Th align="right" className="hidden w-[136px] pl-4 xl:table-cell">
                Do rozliczenia
              </Th>
            )}
            <Th className="hidden w-[136px] pl-4 xl:table-cell">Ostatni kontakt</Th>
            <Th className="w-[166px] pl-4">
              <span className="sr-only">Sygnały</span>
            </Th>
          </Tr>
        </THead>
        <TBody>
          {items.map((c) => (
            <Tr key={c.id} className="relative transition-colors hover:bg-surface">
              <Td className="h-[60px] pl-1">
                <div className="flex min-w-0 items-center gap-3">
                  <ClientTile name={c.name} color={c.color} />
                  <div className="min-w-0">
                    <Link
                      to={`/klienci/${c.id}`}
                      className="block truncate font-semibold text-fg after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-accent"
                    >
                      {c.name}
                    </Link>
                    {c.website && <p className="truncate text-caption text-fg-muted">{websiteLabel(c.website)}</p>}
                  </div>
                </div>
              </Td>
              <Td className="h-[60px] pl-4">
                <OwnerCell client={c} />
              </Td>
              <Td className="h-[60px] pl-4 text-fg-secondary">{activeProjectsLabel(c.stats.activeProjects)}</Td>
              {seesRates && (
                <Td align="right" className="hidden h-[60px] pl-4 xl:table-cell">
                  {/* niezafakturowany czas klienta (jak kafelek profilu; audyt końcowy Fali 6) */}
                  <Mono className={cn('text-[13px]', c.stats.unbilled ? 'text-fg' : 'text-fg-muted')}>
                    {unbilledLabel(c.stats.unbilled)}
                  </Mono>
                </Td>
              )}
              <Td className="hidden h-[60px] pl-4 text-fg-muted xl:table-cell">
                {c.stats.lastContactAt ? (
                  <time dateTime={new Date(c.stats.lastContactAt).toISOString()}>
                    {lastContactLabel(c.stats.lastContactAt)}
                  </time>
                ) : (
                  '–'
                )}
              </Td>
              <Td className="h-[60px] pl-4">
                <ClientSignal client={c} />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function MobileList({ items }: { items: ClientListItem[] }) {
  return (
    <ul className="border-t border-line lg:hidden [&>li:last-child>a]:border-b-0" aria-label="Klienci">
      {items.map((c) => (
        <li key={c.id}>
          <Link
            to={`/klienci/${c.id}`}
            className="flex min-h-[60px] items-center gap-3 border-b border-line py-2.5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
          >
            <ClientTile name={c.name} color={c.color} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body-s font-semibold text-fg">{c.name}</span>
              <span className="truncate text-caption text-fg-muted">
                {[websiteLabel(c.website), c.owner?.name.split(' ')[0]].filter(Boolean).join(' · ') || '–'}
              </span>
            </span>
            <span className="shrink-0">
              <ClientSignal client={c} />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
